/**
 * ClickIt Platform — Branded Loading Screen
 * Used as a full-page loading indicator while data is being fetched.
 */

import React from 'react';

interface LoadingScreenProps {
  /** Optional message below the spinner */
  message?: string;
  /** Show as full-page overlay or inline */
  fullPage?: boolean;
}

export function LoadingScreen({ message = 'Loading your workspace...', fullPage = true }: LoadingScreenProps) {
  const wrapper = fullPage
    ? 'fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
    : 'flex flex-col items-center justify-center py-20';

  return (
    <div className={wrapper}>
      {/* Logo / Brand mark */}
      <div className="relative mb-8">
        {/* Outer ring pulse */}
        <div className="absolute inset-0 w-20 h-20 rounded-full border-2 border-blue-500/20 animate-ping" />
        {/* Spinner ring */}
        <div className="w-20 h-20 rounded-full border-[3px] border-slate-600 border-t-blue-500 animate-spin" />
        {/* Center icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
          </svg>
        </div>
      </div>

      {/* Brand name */}
      <h1 className="text-2xl font-bold text-white tracking-wide mb-2">
        Click<span className="text-blue-400">It</span>
      </h1>

      {/* Loading message */}
      <p className="text-sm text-slate-400 animate-pulse">{message}</p>

      {/* Progress dots */}
      <div className="flex gap-1.5 mt-6">
        <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

/**
 * Inline loading spinner for sections / cards
 */
export function LoadingSpinner({ size = 'md', message }: { size?: 'sm' | 'md' | 'lg'; message?: string }) {
  const sizeMap = { sm: 'w-5 h-5', md: 'w-8 h-8', lg: 'w-12 h-12' };
  const borderMap = { sm: 'border-2', md: 'border-[3px]', lg: 'border-4' };

  return (
    <div className="flex flex-col items-center justify-center gap-3 py-8">
      <div className={`${sizeMap[size]} rounded-full ${borderMap[size]} border-slate-600 border-t-blue-500 animate-spin`} />
      {message && <p className="text-sm text-slate-400">{message}</p>}
    </div>
  );
}
